import { CalendarDays, ClipboardPen, Download, FilePlus2, Layers, type LucideIcon, Scale, Search, SlidersHorizontal, Truck } from "lucide-react";

import { pestanaVisible, seccionesVisibles } from "@/app/menu-visible";
import { puede, type Usuario } from "@/features/acceso/api";
import { normalizar } from "@/features/cotizador/components/hoja/catalogo-global";
import { alcanzaLaLinea, LINEAS } from "@/features/produccion/lineas";

import { type AccionDelTablero, PESTANAS_DEL_TABLERO } from "./tablero";

/** Una fila del buscador: una acción, una pantalla o un registro encontrado en el servidor. */
export type Opcion = {
  id: string;
  grupo: "Acciones" | "Ir a" | "Pedidos" | "Cotizaciones" | "Clientes" | "Referencias";
  label: string;
  detalle?: string | null;
  to: string;
  icon?: LucideIcon;
  /** Lo que también la encuentra sin verse: el nombre de la pantalla que una acción reemplaza. */
  palabras?: string;
};

/**
 * **Las acciones de todos los días** (#30), cada una con el permiso que pide su pantalla. Solo verbos:
 * ir a Pedidos o a Validar son pantallas y salen en «Ir a», con la misma regla que el menú lateral.
 */
function acciones(usuario: Usuario | null | undefined, ubicacion: string): Opcion[] {
  const out: Opcion[] = [];
  // Con `cotizador.ver` se cotiza en el Cotizador; sin él, a mano (la misma regla que «Nueva cotización»).
  const cotizar = puede(usuario, "cotizador.ver") ? "/comercial/cotizaciones/cotizador" : puede(usuario, "cotizaciones.editar") ? "/comercial/cotizaciones/nueva" : null;
  if (cotizar) out.push({ id: "nueva-cotizacion", grupo: "Acciones", label: "Nueva cotización", to: cotizar, icon: FilePlus2 });
  out.push(...accionesDePlanta(usuario, ubicacion), ...crearReferencias(usuario, ubicacion));
  // Crear y anular una remisión piden `pedidos.editar` (Comercial › Remisiones).
  if (puede(usuario, "pedidos.editar")) out.push({ id: "nueva-remision", grupo: "Acciones", label: "Nueva remisión", to: "/comercial/remisiones?nueva=1", icon: Truck });
  if (puede(usuario, "cotizaciones.editar")) {
    out.push({ id: "traer-de-siigo", grupo: "Acciones", label: "Traer de Siigo", detalle: "El número de la cotización y nace el pedido", to: "/comercial/traer-de-siigo", icon: Download });
  }
  return out;
}

function accionesDePlanta(usuario: Usuario | null | undefined, ubicacion: string): Opcion[] {
  const out: Opcion[] = [];
  for (const linea of lineasDePlanta(usuario, ubicacion)) {
    // Calados y Fundidos viven en el tablero único (Ola A 25-oct): sus acciones llevan también su `&linea=`
    // («?accion=» primero: así el buscador sigue sin pisar un formulario abierto)
    const tablero = linea.sigla === "CA" || linea.sigla === "FU";
    const to = `/produccion/lineas/${tablero ? "BA" : linea.sigla}`;
    const deLinea = tablero ? `&linea=${linea.sigla}` : "";
    if (linea.sigla === "BA") {
      out.push(...accionesDelTablero(usuario, to, linea.nombre));
      continue;
    }
    if (puede(usuario, "produccion.registrar") && linea.sigla !== "PG") {
      out.push({ id: `anotar-parte-${linea.sigla}`, grupo: "Acciones", label: `Anotar parte · ${linea.nombre}`, to: `${to}?accion=parte${deLinea}`, icon: ClipboardPen });
    }
    if (puede(usuario, "produccion.programar")) {
      out.push({ id: `programar-${linea.sigla}`, grupo: "Acciones", label: `${linea.sigla === "PG" ? "Nueva tanda" : "Programar"} · ${linea.nombre}`, to: `${to}?accion=programar${deLinea}`, icon: ClipboardPen });
    }
  }
  return out;
}

/**
 * **Baldosa es el tablero** (ronda 20-oct, ola 2 §5): sus acciones llevan `?accion=` y sus pestañas
 * `?pestana=`, y `TableroPage` las atiende una vez. Cada una con el permiso del botón que reemplaza.
 */
function accionesDelTablero(usuario: Usuario | null | undefined, to: string, nombre: string): Opcion[] {
  const a = (id: string, label: string, accion: AccionDelTablero,palabras: string, icon: LucideIcon): Opcion =>
    ({ id: `tablero-${id}`, grupo: "Acciones", label: `${label} · ${nombre}`, to: `${to}?accion=${accion}`, icon, palabras });
  const out: Opcion[] = [];
  if (puede(usuario, "produccion.programar")) out.push(a("programar", "Programar ítems", "programar", "programar por programar prensa artesano", ClipboardPen));
  if (puede(usuario, "produccion.registrar")) out.push(a("parte", "Anotar el parte de hoy", "parte", "anotar parte ingresar producción", ClipboardPen));
  // La misma regla del botón «Conciliar producción» de la barra (`EnlaceConciliar`).
  if (puede(usuario, "produccion.programar") && puede(usuario, "pedidos.editar")) out.push(a("conciliar", "Conciliar producción", "conciliar", "conciliar saldo inicial pedidos que ya corren", Scale));
  out.push(
    a("parametros", "Producir de más / Parámetros", "parametros", "producir de más margen parámetros descuento tipos pago", SlidersHorizontal),
    a("buscar", "Buscar pedido en el tablero", "buscar", "buscar pedido cliente cotización lupa", Search),
    a("hoy", "Ir a hoy en el tablero", "hoy", "ir a hoy fecha calendario", CalendarDays),
  );
  return out;
}

/** Las pestañas del tablero, en «Ir a» detrás de las pantallas (las flechas recorren los grupos de corrido). */
function pestanasDelTablero(usuario: Usuario | null | undefined, ubicacion: string): Opcion[] {
  const baldosa = lineasDePlanta(usuario, ubicacion).find((l) => l.sigla === "BA");
  if (!baldosa) return [];
  return PESTANAS_DEL_TABLERO.map(([pestana, label]) => ({
    id: `tablero-pestana-${pestana}`, grupo: "Ir a", label: `${baldosa.nombre} › ${label}`,
    to: `/produccion/lineas/BA?pestana=${pestana}`, icon: Layers, palabras: "pestaña tablero",
  }));
}

/** Sin contexto se nombran todas las líneas permitidas: jamás se escoge una por el orden del menú. */
function lineasDePlanta(usuario: Usuario | null | undefined, ubicacion: string) {
  if (!puede(usuario, "produccion.ver")) return [];
  const actual = lineaDePantalla(ubicacion);
  return (actual ? [actual] : LINEAS).filter((l) => alcanzaLaLinea(l.sigla, usuario?.production_lines));
}

function crearReferencias(usuario: Usuario | null | undefined, ubicacion: string): Opcion[] {
  const out: Opcion[] = [];
  const actual = lineaDePantalla(ubicacion);
  // Las entradas del asistente son distintas: ventas no necesita ni recibe acceso a Costos.
  const costos = puede(usuario, "costos.ver") && puede(usuario, "costos.editar") && puede(usuario, "catalogo.editar");
  const comercial = puede(usuario, "cotizador.ver") && puede(usuario, "cotizaciones.editar");
  const destinoReferencia = costos && (ubicacion.startsWith("/costos") || !comercial)
    ? "/costos/referencias"
    : comercial ? "/comercial/cotizaciones/cotizador" : null;
  if (destinoReferencia) for (const linea of actual ? [actual] : LINEAS) {
    out.push({ id: `crear-referencia-${linea.code}`, grupo: "Acciones", label: `Crear referencia · ${linea.nombre}`, to: `${destinoReferencia}?linea=${linea.code}&crear=referencia`, icon: FilePlus2 });
  }
  return out;
}

/** Alertas y Pedidos van primero con el buscador vacío. */
const PRIMERO = ["/pendientes", "/comercial/flujo"];

/**
 * **Las pantallas, las mismas del menú lateral** y con su misma regla de permisos (`app/menu-visible`):
 * el buscador no abre una puerta que el menú esconde.
 */
function pantallas(usuario: Usuario | null | undefined): Opcion[] {
  const ve = pestanaVisible(usuario);
  const out: Opcion[] = [];
  for (const item of seccionesVisibles(usuario)) {
    out.push({ id: item.to, grupo: "Ir a", label: item.label, to: item.to, icon: item.icon,
      ...(item.to === "/pendientes" ? { palabras: "Pendientes Validar" } : {}),
    });
    for (const c of item.children ?? []) {
      if (c.soon || !ve(c)) continue;
      const to = `${item.to}/${c.to}`;
      out.push({ id: to, grupo: "Ir a", label: `${item.label} › ${c.label}`, detalle: c.hint, to, icon: c.icon });
    }
  }
  const lugar = (o: Opcion) => (PRIMERO.includes(o.to) ? PRIMERO.indexOf(o.to) : PRIMERO.length);
  return out.sort((a, b) => lugar(a) - lugar(b));
}

/**
 * Las acciones y las pantallas de quien mira, **sin repetir**: una pantalla a la que ya lleva una
 * acción («Cotizador de baldosa» y «Nueva cotización») sale una vez, como acción, y su nombre la
 * sigue encontrando.
 */
export function opcionesFijas(usuario: Usuario | null | undefined, ubicacion = "/"): Opcion[] {
  const hechas = acciones(usuario, ubicacion);
  const porDestino = new Map(hechas.map((a) => [a.to, a]));
  const resto = pantallas(usuario).filter((p) => {
    const accion = porDestino.get(p.to);
    if (accion) accion.palabras = `${accion.palabras ?? ""} ${p.label}`;
    return !accion;
  });
  return [...hechas, ...resto, ...pestanasDelTablero(usuario, ubicacion)];
}

/** Solo contexto explícito de la pantalla o su selector de línea; no el primer permiso disponible. */
export function lineaDePantalla(ubicacion: string) {
  const [ruta, query = ""] = ubicacion.split("?");
  const sigla = ruta?.match(/^\/produccion\/lineas\/(BA|CA|FU|CE|PG)(?:\/|$)/)?.[1];
  const codigo = new URLSearchParams(query).get("linea");
  // el tablero único con Calados o Fundidos elegidos: el contexto es esa línea
  if (sigla === "BA" && (codigo === "CA" || codigo === "FU")) return LINEAS.find((l) => l.sigla === codigo);
  if (sigla) return LINEAS.find((l) => l.sigla === sigla);
  if (codigo) return LINEAS.find((l) => l.code === codigo || l.sigla === codigo);
  const segmento = ruta?.split("/").at(-1);
  return LINEAS.find((l) => l.code === segmento);
}

/** Las que tienen todas las palabras escritas, en cualquier orden y sin tildes; vacío = todas. */
export function filtrar(opciones: Opcion[], texto: string): Opcion[] {
  const palabras = normalizar(texto).split(/\s+/).filter(Boolean);
  if (!palabras.length) return opciones;
  return opciones.filter((o) => {
    const dentro = normalizar(`${o.label} ${o.detalle ?? ""} ${o.palabras ?? ""}`);
    return palabras.every((p) => dentro.includes(p));
  });
}
